import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api, apiErrorMessage } from "../api/client";
import { theme, btnPrimary, btnSecondary, inputStyle } from "../theme";

// Authenticator-app enrolment: scan the QR (or type the key), confirm a code,
// save the recovery codes. Used by the forced-enrolment gate after a password
// sign-in, and by the profile page when replacing an authenticator — in which
// case `proof` carries the password and current code the API insists on.

interface Setup { secret: string; otpauthUri: string }
// The refresh token itself is set by the API as an HttpOnly cookie; the body only says there is one.
interface Confirmed { recoveryCodes: string[]; accessToken: string; refreshable?: boolean }

export function TotpEnrolment({ proof, onDone, onCancel }: {
  proof?: { password: string; code: string };
  /** Called once the user has acknowledged their recovery codes. */
  onDone: (session: { accessToken: string }) => void;
  onCancel?: () => void;
}) {
  const [setup, setSetup]       = useState<Setup | null>(null);
  const [qr, setQr]             = useState<string | null>(null);
  const [code, setCode]         = useState("");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  const [result, setResult]     = useState<Confirmed | null>(null);
  const [copied, setCopied]     = useState(false);
  const [showKey, setShowKey]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError("");
    api.post<Setup>("/auth/mfa/totp/setup", proof ?? {})
      .then(async (s) => {
        if (cancelled) return;
        setSetup(s);
        try {
          setQr(await QRCode.toDataURL(s.otpauthUri, { margin: 1, width: 200, errorCorrectionLevel: "M" }));
        } catch {
          setQr(null); // the key can still be typed in
        }
      })
      .catch((e) => { if (!cancelled) setError(apiErrorMessage(e, "Could not start authenticator setup.")); });
    return () => { cancelled = true; };
  // A new proof means a new setup; the object identity is what changes.
  }, [proof]);

  async function confirm() {
    if (!code.trim()) return;
    setBusy(true); setError("");
    try {
      const r = await api.post<Confirmed>("/auth/mfa/totp/confirm", { code: code.trim() });
      setResult(r);
    } catch (e) {
      setError(apiErrorMessage(e, "That code was not accepted. Try the next one shown in the app."));
    } finally {
      setBusy(false);
    }
  }

  function copyCodes() {
    if (!result) return;
    navigator.clipboard?.writeText(result.recoveryCodes.join("\n")).then(() => setCopied(true)).catch(() => {});
  }

  function downloadCodes() {
    if (!result) return;
    const text = `Abacas Security recovery codes\nEach code signs you in once if you lose your authenticator app. Keep them somewhere safe.\n\n${result.recoveryCodes.join("\n")}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url; a.download = "abacas-recovery-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  }

  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6, display: "block" };
  const hint: React.CSSProperties = { fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 };

  if (result) {
    return (
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Authenticator app enabled</div>
        <div style={{ ...hint, marginBottom: 14 }}>
          These recovery codes each sign you in <strong>once</strong> if you lose your phone. This is the only time they are shown —
          save them somewhere safe now, such as your password manager.
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 18px", padding: 14, borderRadius: theme.radius.md,
          background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 14, color: theme.color.heading, letterSpacing: "0.04em", marginBottom: 14,
        }}>
          {result.recoveryCodes.map((c) => <div key={c}>{c}</div>)}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
          <button type="button" onClick={copyCodes} style={btnSecondary}>{copied ? "Copied" : "Copy"}</button>
          <button type="button" onClick={downloadCodes} style={btnSecondary}>Download</button>
        </div>
        <button type="button" onClick={() => onDone({ accessToken: result.accessToken })} style={btnPrimary}>
          I have saved my recovery codes
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); void confirm(); }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Set up your authenticator app</div>
      <div style={{ ...hint, marginBottom: 16 }}>
        Install an authenticator app if you do not have one — Microsoft Authenticator, Google Authenticator, Authy and 1Password all work —
        then scan this code with it. The app will show a new six-digit code every 30 seconds; you will need it each time you sign in.
      </div>

      {error && !setup && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div>}

      {setup && (
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ width: 200, height: 200, borderRadius: theme.radius.md, background: "#fff", padding: 6, border: `1px solid ${theme.color.border}`, flexShrink: 0 }}>
            {qr
              ? <img src={qr} alt="Authenticator setup QR code" width={188} height={188} style={{ display: "block" }} />
              : <div style={{ ...hint, padding: 12 }}>QR code unavailable — enter the key below by hand.</div>}
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <button type="button" onClick={() => setShowKey((v) => !v)} style={{ ...btnSecondary, fontSize: 12, padding: "6px 12px" }}>
              {showKey ? "Hide setup key" : "Can't scan? Show the setup key"}
            </button>
            {showKey && (
              <div style={{ marginTop: 10 }}>
                <div style={hint}>In the app choose "enter a setup key" and type this (time-based, 6 digits):</div>
                <code style={{ display: "block", marginTop: 6, padding: "8px 10px", borderRadius: theme.radius.sm, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, fontSize: 13, letterSpacing: "0.08em", wordBreak: "break-all", color: theme.color.heading }}>
                  {setup.secret.replace(/(.{4})/g, "$1 ").trim()}
                </code>
              </div>
            )}
          </div>
        </div>
      )}

      <label style={label} htmlFor="totp-confirm-code">Enter the code the app shows now</label>
      <input
        id="totp-confirm-code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, "").slice(0, 7))}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123 456"
        disabled={!setup || busy}
        style={{ ...inputStyle, width: 160, fontSize: 18, letterSpacing: "0.12em", textAlign: "center" }}
      />
      {error && setup && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button type="submit" disabled={!setup || busy || code.replace(/\s/g, "").length !== 6} style={{ ...btnPrimary, opacity: !setup || busy || code.replace(/\s/g, "").length !== 6 ? 0.6 : 1 }}>
          {busy ? "Checking…" : "Turn on"}
        </button>
        {onCancel && <button type="button" onClick={onCancel} disabled={busy} style={btnSecondary}>Cancel</button>}
      </div>
    </form>
  );
}
